import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { promotionsApi } from '../services/promotionsApi';
import { PROMO_STATUSES, PromoCodeDetail, PromoCodeSummary, PromoDerivedStatus } from '../types/promotions';
import CursorPagination from '../components/CursorPagination';
import PromoCodeForm from '../components/promotions/PromoCodeForm';
import PromoStatusChip, { AudienceChip } from '../components/promotions/PromoStatusChip';
import { discountText, formatAed, formatExpiry } from '../components/promotions/promoFormat';

const TABS: { key: PromoDerivedStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...PROMO_STATUSES.map((s) => ({ key: s, label: s.charAt(0) + s.slice(1).toLowerCase() })),
];

const PAGE_SIZE = 20;

/** Spec 059 Flow A/D — the promo code list (route /promotions, screen `promotions`). */
export default function PromotionsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<PromoDerivedStatus | 'ALL'>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<PromoCodeSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(async (after: string | null) => {
    if (!auth.token) return;
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await promotionsApi.list({
        status: tab === 'ALL' ? undefined : tab, search: search || undefined, cursor: after ?? undefined, size: PAGE_SIZE,
      }, auth.token);
      setRows((prev) => (after ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor ?? null);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load promo codes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [auth.token, tab, search]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const onCreated = (saved: PromoCodeDetail) => {
    setCreating(false);
    navigate(`/promotions/${saved.id}`, { state: { toast: `Code ${saved.code} created` } });
  };

  const unfiltered = tab === 'ALL' && !search;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>Promo codes</h2>
        <button type="button" className="admin-btn admin-btn-primary" onClick={() => setCreating(true)}>New code</button>
      </div>

      <div className="admin-tabs" role="tablist" style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--admin-border)' }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`promo-tab${tab === t.key ? ' active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-toolbar">
          <input type="text" className="admin-search-input" placeholder="Search by code or name..." aria-label="Search promo codes"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>

        {loading ? (
          <div className="admin-loading" data-testid="promotions-loading"><div className="admin-loading-spinner" /></div>
        ) : error ? (
          <div data-testid="promotions-error">
            <div className="admin-info-banner danger" role="alert">{error}</div>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => fetchPage(null)}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="admin-empty-state" data-testid="promotions-empty">
            {unfiltered ? (
              <>
                <p>No promo codes yet</p>
                <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setCreating(true)}>New code</button>
              </>
            ) : <p>No codes match these filters.</p>}
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Audience</th><th>Discount</th><th>Status</th>
                  <th style={{ textAlign: 'right' }}>Slots used</th><th style={{ textAlign: 'right' }}>Uses / customer</th>
                  <th style={{ textAlign: 'right' }}>Spend</th><th>Expiry</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}><Link to={`/promotions/${p.id}`} className="admin-link">{p.code}</Link></td>
                    <td>{p.name}</td>
                    <td><AudienceChip audience={p.audience} /></td>
                    <td>{discountText(p)}</td>
                    <td><PromoStatusChip status={p.derivedStatus} /></td>
                    <td style={{ textAlign: 'right' }}>{p.holdersCount}/{p.maxHolders}</td>
                    <td style={{ textAlign: 'right' }}>{p.usesPerHolder}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAed(p.spend ?? 0)}</td>
                    <td>{formatExpiry(p.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => fetchPage(cursor)} />}
      </div>

      {creating && auth.token && (
        <PromoCodeForm promo={null} token={auth.token} onClose={() => setCreating(false)} onSaved={onCreated} />
      )}
    </div>
  );
}
